// What every driver is built on (PLAN-CLIENT-SPLIT day 7).
//
// A driver is a subscriber: the store changes, the driver wakes, decides
// whether anything is owed, and dispatches on a delay so a turn is something
// you watch. Two of them share this door — the opponent and the run director
// — and it exists so each has exactly one way to dispatch,
// one way to borrow the camera, and one way to know it has been torn down.
//
// **No React, and so no dependency list.** The hooks these replaced stalled
// four times, every time the same way: an effect that re-ran on every state
// change, a `busy` ref it could set and fail to clear, and a dependency it
// forgot or a callback that was not stable. Here `wake` runs on every store
// change and every `configure`, unconditionally, and the guards live in the
// driver — there is nothing to list and nothing to be unstable.

import type { Branch, Focus, PlanReport, Square } from "src/api";
import type { GameStore } from "src/engine/game/GameStore";
import type { Command } from "src/engine/rules/commands";

/** What the drivers may say to the client, through `Game.on`. */
export interface DriverEmit {
  focus(focus: Focus): void;
  plan(report: PlanReport): void;
  branch(branch: Branch | null): void;
}

/** The door the drivers share. */
export interface DriverDoor {
  readonly store: GameStore;
  /**
   * Where a command goes: the room when one is attached, else the store. The
   * session's door, handed in so a driver and a click take the same road.
   */
  dispatch(command: Command): boolean;
  readonly emit: DriverEmit;
}

/** Point the camera at these squares, if the player allows it. */
export type Look = (squares: readonly number[], why: "move" | "event" | "region") => void;

/**
 * The subscription and the teardown, shared.
 *
 * `wake` is re-entrant by construction: a dispatch from inside it changes the
 * store, which calls back in — so a call while one is running is noted and
 * run once the first returns, rather than nested. That is what React's
 * batching was doing for the hooks, and what the hooks lost when they
 * dispatched from a timer instead.
 */
export abstract class Driver {
  private unsubscribe: (() => void) | null;
  private waking = false;
  private again = false;
  /** True once torn down, so a timer that fires afterwards does nothing. */
  protected disposed = false;

  constructor(protected readonly door: DriverDoor) {
    this.unsubscribe = door.store.subscribe(() => this.wake());
  }

  /** Something changed: decide whether anything is owed. */
  wake(): void {
    if (this.disposed) return;
    if (this.waking) {
      this.again = true;
      return;
    }
    this.waking = true;
    try {
      do {
        this.again = false;
        this.step();
      } while (this.again && !this.disposed);
    } finally {
      this.waking = false;
    }
  }

  /** One pass: the former effect body. */
  protected abstract step(): void;

  /** The camera, borrowed: a `look` the client may honour or not. */
  protected look(squares: readonly number[], why: "move" | "event" | "region"): void {
    this.door.emit.focus({ kind: "look", squares: squares as readonly Square[], why, remember: false });
  }

  /** The camera, handed back. */
  protected returnView(): void {
    this.door.emit.focus({ kind: "return" });
  }

  /** A timer that does nothing once the driver is gone. */
  protected later(fn: () => void, ms: number): void {
    setTimeout(() => {
      if (!this.disposed) fn();
    }, ms);
  }

  dispose(): void {
    this.disposed = true;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}
